/**
 * Döviz/kripto → TL hesaplama sayfaları (eski Laravel: /ekonomi/8877-dolar-kac-tl).
 * Slug ayrıştırma, TL çevrimi ve iç link üretimi.
 */
import type { MarketInstrumentKey, MarketPayload, MarketRateSnapshot } from '@/lib/market-rates'

export type CalcCurrency = {
  key: MarketInstrumentKey
  /** URL parçası: /ekonomi/100-dolar-kac-tl */
  urlSlug: string
  /** Eski Laravel / alternatif yazımlar */
  aliases: string[]
  /** Kısa ad: "Dolar" */
  name: string
  /** Uzun ad: "Amerikan Doları" */
  longName: string
  /** ISO/borsa sembolü */
  symbol: string
  /** USDT bazlı fiyat — TL için USDTRY ile çarpılır */
  usdQuoted: boolean
  /** İlgili haberler için etiket slug'ı */
  newsTagSlug: string
}

export const CALC_CURRENCIES: CalcCurrency[] = [
  {
    key: 'USDTRY',
    urlSlug: 'dolar',
    aliases: ['usd', 'amerikan-dolari'],
    name: 'Dolar',
    longName: 'Amerikan Doları',
    symbol: 'USD',
    usdQuoted: false,
    newsTagSlug: 'dolar',
  },
  {
    key: 'EURTRY',
    urlSlug: 'euro',
    aliases: ['eur', 'avro'],
    name: 'Euro',
    longName: 'Euro',
    symbol: 'EUR',
    usdQuoted: false,
    newsTagSlug: 'euro',
  },
  {
    key: 'GBPTRY',
    urlSlug: 'sterlin',
    aliases: ['gbp', 'ingiliz-sterlini', 'pound'],
    name: 'Sterlin',
    longName: 'İngiliz Sterlini',
    symbol: 'GBP',
    usdQuoted: false,
    newsTagSlug: 'sterlin',
  },
  {
    key: 'GRAMALTINTRY',
    urlSlug: 'gram-altin',
    aliases: ['gram_altin', 'altin'],
    name: 'Gram Altın',
    longName: 'Gram Altın',
    symbol: 'XAU',
    usdQuoted: false,
    newsTagSlug: 'altin',
  },
  {
    key: 'CEYREKALTINTRY',
    urlSlug: 'ceyrek-altin',
    aliases: ['ceyrek_altin', 'ceyrek'],
    name: 'Çeyrek Altın',
    longName: 'Çeyrek Altın',
    symbol: 'XAUQ',
    usdQuoted: false,
    newsTagSlug: 'altin',
  },
  {
    key: 'BTCUSDT',
    urlSlug: 'bitcoin',
    aliases: ['btc'],
    name: 'Bitcoin',
    longName: 'Bitcoin',
    symbol: 'BTC',
    usdQuoted: true,
    newsTagSlug: 'bitcoin',
  },
  {
    key: 'ETHUSDT',
    urlSlug: 'ethereum',
    aliases: ['eth'],
    name: 'Ethereum',
    longName: 'Ethereum',
    symbol: 'ETH',
    usdQuoted: true,
    newsTagSlug: 'ethereum',
  },
  {
    key: 'XRPUSDT',
    urlSlug: 'ripple',
    aliases: ['xrp'],
    name: 'Ripple',
    longName: 'Ripple (XRP)',
    symbol: 'XRP',
    usdQuoted: true,
    newsTagSlug: 'ripple',
  },
]

const CURRENCY_BY_SLUG = new Map<string, CalcCurrency>()
for (const c of CALC_CURRENCIES) {
  CURRENCY_BY_SLUG.set(c.urlSlug, c)
  for (const alias of c.aliases) CURRENCY_BY_SLUG.set(alias, c)
}

export function findCalcCurrency(slugOrAlias: string): CalcCurrency | null {
  return CURRENCY_BY_SLUG.get(slugOrAlias.toLowerCase()) ?? null
}

/** Enstrüman detay slug'ından (dolar, gram_altin…) hesaplanabilir kur bul */
export function findCalcCurrencyByInstrumentKey(key: MarketInstrumentKey): CalcCurrency | null {
  return CALC_CURRENCIES.find((c) => c.key === key) ?? null
}

export type ParsedCalcSlug = { amount: number; currency: CalcCurrency }

const CALC_SLUG_RE = /^(\d{1,12})-([a-z][a-z_-]*)-kac-tl$/

/** "8877-dolar-kac-tl" → { amount: 8877, currency: Dolar } */
export function parseCalcSlug(slug: string): ParsedCalcSlug | null {
  const m = CALC_SLUG_RE.exec(slug.toLowerCase())
  if (!m) return null
  const amount = Number(m[1])
  if (!Number.isSafeInteger(amount) || amount < 1) return null
  const currency = findCalcCurrency(m[2])
  if (!currency) return null
  return { amount, currency }
}

export function calcHref(amount: number, currency: CalcCurrency): string {
  return `/ekonomi/${amount}-${currency.urlSlug}-kac-tl`
}

export type CalcRate = {
  /** 1 birimin TL karşılığı (orta kur) */
  unitTry: number
  /** TL alış — yalnızca TRY bazlı kurlarda */
  buyTry: number | null
  /** TL satış — yalnızca TRY bazlı kurlarda */
  sellTry: number | null
  changePct: number | null
  fetchedAt: string | null
}

function snapshotOk(row: MarketRateSnapshot | null | undefined): row is MarketRateSnapshot {
  return !!row && Number.isFinite(row.rate) && row.rate > 0
}

/** Kurun güncel TL karşılığını hesapla (USDT bazlılar USDTRY üzerinden) */
export function resolveCalcRate(
  currency: CalcCurrency,
  payload: MarketPayload | null,
): CalcRate | null {
  if (!payload) return null
  const row = payload[currency.key]
  if (!snapshotOk(row)) return null

  if (!currency.usdQuoted) {
    return {
      unitTry: row.rate,
      buyTry: row.buy != null && Number.isFinite(row.buy) && row.buy > 0 ? row.buy : null,
      sellTry: row.sell != null && Number.isFinite(row.sell) && row.sell > 0 ? row.sell : null,
      changePct: row.changePct,
      fetchedAt: row.fetchedAt ?? null,
    }
  }

  const usd = payload.USDTRY
  if (!snapshotOk(usd)) return null
  return {
    unitTry: row.rate * usd.rate,
    buyTry: null,
    sellTry: null,
    changePct: row.changePct,
    fetchedAt: row.fetchedAt ?? null,
  }
}

/** tr-TR para formatı: 422.421,81 */
export function formatTry(value: number, decimals = 2): string {
  if (!Number.isFinite(value)) return '----'
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

/** tr-TR miktar formatı: 8.877 (ondalıksız tam sayı) */
export function formatAmount(value: number): string {
  if (!Number.isFinite(value)) return '0'
  return new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 8 }).format(value)
}

/** Sayfa içi "X {kur} Kaç TL" hızlı linkleri */
export const POPULAR_AMOUNTS = [1, 10, 100, 250, 500, 1000, 5000, 10000] as const

/** Deterministik ek miktarlar — eski Laravel'deki çeşitlendirilmiş linkler gibi */
export function relatedAmounts(base: number, count = 5): number[] {
  const out = new Set<number>()
  let seed = base
  while (out.size < count) {
    // Basit LCG — build/SSR tutarlılığı için deterministik
    seed = (seed * 48271) % 2147483647
    const candidate = (seed % 9900) + 100
    if (candidate !== base) out.add(candidate)
  }
  return [...out]
}
